import type { IntlShape } from 'react-intl';

type FormatMessage = IntlShape['formatMessage'];

/**
 * Friendly display label for the pseudo-boolean and single-numeric-value filter attributes
 * that aren't real multi-value Typesense facets (isDriversLicenseRequired, excludeSaved,
 * salaryRangeMin, etc.). These carry one literal value ('true'/'false', a number) that the
 * generic facet-label formatters (format-filter-label.ts) show as-is — "True", "5000" — instead
 * of the message the filter's own toggle/slider uses. Shared by AllFiltersModal's applied-filter
 * chips and TopFilters' pill row so the two stay in sync.
 *
 * `rawKey` is the literal filters-object key, which for ExcludeBooleanToggle-backed attributes
 * (isRecruitmentCompany, isConsultingCompany) is `exclude_<attribute>` rather than the bare
 * attribute name — their label already reads as the exclude action ("Hide Recruitment Agency
 * Postings"), so the prefix is stripped before matching and doesn't change which label comes back.
 *
 * Returns null when `rawKey` isn't one of these, so callers fall back to their normal
 * facet-label path.
 */
export function getKnownSingleValueFilterLabel(
  rawKey: string,
  value: string,
  formatMessage: FormatMessage,
): string | null {
  const attribute = rawKey.startsWith('exclude_')
    ? rawKey.slice('exclude_'.length)
    : rawKey.startsWith('only_')
      ? rawKey.slice('only_'.length)
      : rawKey;

  switch (attribute) {
    case 'salaryRangeMin':
      return `€${value}+`;
    case 'vacationWeeks':
      return formatMessage(
        { id: 'search.refinements.vacationWeeks.chip', defaultMessage: '{count}+ vacation weeks', description: 'Applied-filter chip label for the minimum vacation weeks filter' },
        { count: value },
      );
    case 'companyHeadcountRangeMax':
      return formatMessage(
        { id: 'search.refinements.headcount.chip', defaultMessage: 'Up to {count} employees', description: 'Applied-filter chip label for the max company headcount filter' },
        { count: value },
      );
    case 'isDriversLicenseRequired':
      return formatMessage({ id: 'search.refinements.isDriversLicenseRequired.filter', defaultMessage: "Driver's Licence Required" });
    case 'isOpenApplication':
      return formatMessage({ id: 'search.refinements.isOpenApplication.filter', defaultMessage: 'Open Application' });
    case 'isRecruitmentCompany':
      return formatMessage({ id: 'search.refinements.excludeRecruitmentCompany.filter', defaultMessage: 'Hide Recruitment Agency Postings' });
    case 'isConsultingCompany':
      return formatMessage({ id: 'search.refinements.excludeConsultingCompany.filter', defaultMessage: 'Hide Consulting Company Postings' });
    case 'excludeSaved':
      return formatMessage({ id: 'search.refinements.excludeSaved.label', defaultMessage: 'Exclude Saved Jobs' });
    case 'excludeViewed':
      return formatMessage({ id: 'search.refinements.excludeViewed.label', defaultMessage: 'Exclude Viewed Jobs' });
    case 'excludeApplied':
      return formatMessage({ id: 'search.refinements.excludeApplied.label', defaultMessage: 'Exclude Applied Jobs' });
    default:
      return null;
  }
}
